import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";

import useRedirect from "../hooks/useRedirect";

const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within a AuthProvider");
  return context;
};

let initialPageLoad = true;
export default function AuthProvider({ children }) {
  const _userData = loadLocalStorageUserData();

  /** @type {[UserData, any]} */
  const [userData, setUserData] = useState(_userData || {});
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(_userData?.accessToken));
  const location = useLocation();
  const redirect = useRedirect();

  const loggedIn = useCallback((newUserData) => {
    saveLocalStorageUserDate(newUserData);
    setUserData(newUserData);
    setIsLoggedIn(true);
  }, []);

  const loggedOut = useCallback(() => {
    saveLocalStorageUserDate({});
    setUserData({});
    setIsLoggedIn(false);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      if (initialPageLoad) {
        initialPageLoad = false;
        if (location.pathname !== "/login") {
          // stay in the page (except for login page) if we are logged in previously when the page is just loaded
          return;
        }
      }

      // Auth status is changing from logged out to logged in
      redirect("/live", { useReturn: true });
    }
    // Auth status is changing from logged in to logged out
    else redirect("/login", { makeReturn: true });
  }, [isLoggedIn]);

  const contextValue = useMemo(
    () => ({
      loggedIn,
      loggedOut,
      userData,
      isLoggedIn,
    }),
    [userData, isLoggedIn],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

const loadLocalStorageUserData = () => JSON.parse(localStorage.getItem("auth") || "{}");
const saveLocalStorageUserDate = (userData) => localStorage.setItem("auth", JSON.stringify(userData));

/**
 * @typedef {{
 *  accessToken: string,
 *  uid: number,
 *  userId: number,
 *  email: string,
 *  cell: string,
 *  first_name: string,
 *  last_name: string,
 *  avatar: string,
 *  org_id: number,
 *  role_id: number,
 *  email_confirmed: boolean,
 *  cell_confirmed: boolean,
 *  org: {
 *    oid: number,
 *    name: string,
 *    logo: string,
 *  }
 * }} UserData
 */
